import { memo } from 'react'
import cn from 'classnames'

import { Icon } from '~/shared/ui/Icon'
import { VStack } from '~/shared/ui/VStack'
import { Text } from '~/shared/ui/Text'

import {
  getPlaceholderMascotSrc,
  PLACEHOLDER_ILLUSTRATION,
  PLACEHOLDER_SIZE_CONFIG,
} from './config'
import type { PlaceholderProps } from './types'

import styles from './Placeholder.module.scss'

/**
 * Пустое/ошибочное состояние (основатель, п.20): раньше многие шторки и секции без данных
 * оставались просто пустыми — теперь у любого пустого состояния есть заглушка с иллюстрацией,
 * заголовком и (опционально) действием. `name` выбирает картинку из мапы в config.ts.
 * Без карточки-обёртки внутри — так компонент одинаково хорошо смотрится и в шторке
 * (`BottomSheet.Content`), и на весь экран (`Screen.Content`).
 */
export const Placeholder = memo(function Placeholder(props: PlaceholderProps) {
  const { name, title, description, action, size = 'M' } = props

  const illustration = PLACEHOLDER_ILLUSTRATION[name]
  const sizeConfig = PLACEHOLDER_SIZE_CONFIG[size]

  return (
    <div className={cn(styles.root, styles[`size_${size}`])}>
      <VStack gap='SMALL_L' align='CENTER'>
        <div
          className={cn(
            styles.illustration,
            illustration.kind === 'ICON' && styles.illustration_ICON,
          )}
          style={{ width: sizeConfig.illustration, height: sizeConfig.illustration }}
        >
          {illustration.kind === 'MASCOT' ? (
            <img
              className={styles.mascot}
              src={getPlaceholderMascotSrc(illustration.pose, size)}
              alt=''
            />
          ) : (
            <Icon name={illustration.iconName} size={sizeConfig.icon} strokeWidth={1.5} />
          )}
        </div>
        <VStack gap='SMALL_S' align='CENTER'>
          <Text variant={size === 'S' ? 'BODY_S' : 'BODY'} align='CENTER'>
            {title}
          </Text>
          {description && (
            <Text variant='CAPTION' color='SECONDARY' align='CENTER'>
              {description}
            </Text>
          )}
        </VStack>
        {action}
      </VStack>
    </div>
  )
})
